package dev.lingvo.site.pages.app.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.shared.CreateWebhookRequest
import dev.lingvo.shared.Role
import dev.lingvo.shared.WebhookDto
import dev.lingvo.shared.WebhookEvent
import dev.lingvo.shared.atLeast
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCheckbox
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LOneTimeSecret
import dev.lingvo.site.ds.LPlusIcon
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LWebhookIcon
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.enterRise
import dev.lingvo.site.ds.enterStaggered
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectSettingsSurface
import dev.lingvo.site.pages.app.projects.slug.settings.SettingsFormMeasure
import dev.lingvo.site.pages.app.projects.slug.settings.settingsOperationResult
import dev.lingvo.site.state.WorkspaceSelection
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

/**
 * Telling another system when something happens here.
 *
 * `locale.completed` is the one that earns the feature: "German hit 100%, open the release PR" is a
 * thing teams currently do by remembering to check.
 *
 * The signing secret is shown **once**, at creation, and never again — only its hash is stored,
 * because the receiver is the party that needs to keep it and we only ever compare.
 */
@Page("/app/settings/webhooks")
@Layout(".components.layouts.AppLayout")
@Composable
fun WebhooksPage() {
    val scope = rememberCoroutineScope()
    val workspace = WorkspaceSelection.current()
    val slug = workspace?.slug

    var refreshKey by remember { mutableStateOf(0) }
    var url by remember { mutableStateOf("") }
    var events by remember { mutableStateOf(setOf(WebhookEvent.LOCALE_COMPLETED)) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var created by remember { mutableStateOf<WebhookDto?>(null) }
    // Removing a webhook is irreversible and silent on the receiving side: deliveries simply stop,
    // and the secret cannot be recovered to recreate the same endpoint. It gets a confirmation that
    // names the exact URL, like every other destructive action in the product.
    var deleting by remember { mutableStateOf<WebhookDto?>(null) }

    ProjectSettingsSurface {
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            PageHeader(
                t("app.settings.webhooks.webhooks", "Webhooks"),
                t("app.settings.webhooks.have_lingvo_tell_your_systems_when", "Have Lingvo tell your systems when something happens."),
                mark = { LWebhookIcon() },
                actions = {
                    if (workspace == null || workspace.role.atLeast(Role.ADMIN)) {
                        // Secondary, and named for what it does. The add form is unconditionally
                        // mounted directly below with its own accent submit, so an accent "Add a
                        // webhook" up here was the second primary on the screen — and the louder of
                        // the two added nothing: it moves the caret into the URL field. Same words
                        // as the empty state's control below, because it is the same action.
                        LButton(
                            t("app.settings.webhooks.name_a_url", "Name a URL"),
                            { (document.querySelector("#$WEBHOOK_FORM_ID input") as? HTMLElement)?.focus() },
                            variant = LButtonVariant.SECONDARY,
                            leadingIcon = { LPlusIcon() },
                        )
                    }
                },
            )

            // `WebhookRoutes` is owners and admins on every verb, so a translator's load could only ever
            // come back 403 — after a wait, and reading as a fault. The answer is about their role, so
            // it is stated as one, the way every sibling settings surface states it.
            if (workspace != null && !workspace.role.atLeast(Role.ADMIN)) {
                LAlert(
                    title = t("app.settings.webhooks.webhooks_are_managed_by_an_owner", "Webhooks are managed by an owner or admin"),
                    description = t("app.settings.webhooks.a_webhook_sends_this_workspaces_activity", "A webhook sends this workspace's activity to another system, so only owners " +
                        "and admins can add or change one."),
                    variant = LAlertVariant.INFO,
                )
                return@Column
            }

            // Below the refusal, so the refused request is never made. `settings/Usage.kt` orders it
            // this way for the same reason: a load above the gate still goes out and still comes back
            // 403, which is the wait the check exists to remove.
            // `loadReloadable`, not `loadAsync(slug, refreshKey)`. The counter as an identity key
            // discarded the list before the re-read was sent, so adding a webhook collapsed the
            // list of existing endpoints to skeleton bars at the moment the reader wanted to see
            // their new one land — and a delete destroyed the row whose menu had just been used.
            val reloadable = loadReloadable(slug, reloadToken = refreshKey) {
                slug?.let { ApiClient.webhooks(it) }
            }

            created?.let { hook ->
                LCard(Modifier.fillMaxWidth().enterRise()) {
                    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                        LAlert(
                            t("app.settings.webhooks.copy_this_signing_secret_now", "Copy this signing secret now"),
                            t("app.settings.webhooks.it_is_shown_once_lingvo_stores", "It is shown once. Lingvo stores only a hash — your receiver is the side that " +
                                "needs to keep it."),
                            variant = LAlertVariant.WARNING,
                        )
                        LOneTimeSecret(
                            value = hook.secret.orEmpty(),
                            label = tr("app.settings.webhooks.signing_secret", "Signing secret"),
                            copyLabel = t("app.settings.webhooks.copy_secret", "Copy secret"),
                            onCopied = { message = null },
                            onCopyFailed = {
                                message = tr(
                                    "app.settings.webhooks.couldnt_copy_the_secret_select_it",
                                    "Couldn't reach the clipboard. Select the secret above and copy " +
                                        "it manually before leaving.",
                                )
                            },
                        )
                        LMuted(
                            t("app.settings.webhooks.every_request_carries_x_lingvo_signature_2", "Every request carries X-Lingvo-Signature: sha256=… computed over the exact " +
                                "bytes we send. Verify it before trusting the body."),
                        )
                        LButton(t("app.settings.webhooks.done", "Done"), { created = null })
                    }
                }
            }

            // Announced, because it only ever appears in answer to Add or Remove: without a live region
            // a screen reader sits on the button it just pressed and is told nothing at all.
            message?.let {
                LAlert(
                    t("app.settings.webhooks.that_didnt_work", "That didn't work"),
                    it,
                    variant = LAlertVariant.WARNING,
                    live = true,
                )
            }

            // **The form above the list.** Adding a webhook is why somebody opens this page, and the
            // form sat under however many hooks already existed — on a workspace with six, below the
            // fold. The list is the evidence; the form is the task.
            LCard(Modifier.fillMaxWidth().maxWidth(SettingsFormMeasure).attr("id", WEBHOOK_FORM_ID)) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LSectionTitle(
                        t("app.settings.webhooks.add_a_webhook", "Add a webhook"),
                        icon = { LWebhookIcon() },
                    )
                    LTextField(
                        url,
                        { url = it },
                        Modifier.fillMaxWidth(),
                        label = t("app.settings.webhooks.url", "URL"),
                        placeholder = "https://ci.example.com/lingvo",
                    )
                    // https only, and the reason is stated rather than enforced silently.
                    LMuted(t("app.settings.webhooks.must_be_https_the_payload_names", "Must be https: the payload names your projects, languages and people."))

                    WebhookEvent.entries.forEach { event ->
                        LCheckbox(
                            checked = event in events,
                            onCheckedChange = { on -> events = if (on) events + event else events - event },
                            label = tf("app.settings.webhooks.event_and_description", "{event} — {description}", "event" to event.id, "description" to event.description),
                            enabled = !busy,
                        )
                    }

                    LButton(
                        t("app.settings.webhooks.add_webhook", "Add webhook"),
                        {
                            scope.launch {
                                busy = true
                                message = settingsOperationResult {
                                    ApiClient.createWebhook(
                                        slug!!,
                                        CreateWebhookRequest(url.trim(), events.toList()),
                                    )
                                }.fold({ created = it; url = ""; refreshKey++; null }, { it.message })
                                busy = false
                            }
                        },
                        enabled = url.isNotBlank() && events.isNotEmpty() && !busy,
                        loading = busy,
                    )
                }
            }

            // Beside the retained list, never in place of it.
            reloadable.refreshError?.let { failure ->
                LAlert(
                    t("app.settings.webhooks.showing_the_last_list_that_loaded", "Showing the last list that loaded"),
                    failure.readable(),
                    variant = LAlertVariant.WARNING,
                    dense = true,
                    live = true,
                )
            }

            LAsyncContent(
                reloadable.state,
                rows = 2,
                loadingLabel = t("app.settings.webhooks.loading_webhooks", "Loading webhooks"),
                subject = t("app.settings.webhooks.webhooks_2", "webhooks"),
                onRetry = { refreshKey++ },
            ) { loaded ->
                val hooks = loaded.orEmpty()
                if (hooks.isEmpty()) {
                    LEmptyState(
                        t("app.settings.webhooks.no_webhooks_yet", "No webhooks yet"),
                        t("app.settings.webhooks.add_one_to_hear_when_a", "Add one to hear when a language is finished, a QA check starts failing, or " +
                            "an import lands."),
                        Modifier.fillMaxWidth(),
                        LEmptyStateIcon.WEBHOOK,
                    ) {
                        // An empty state that invites one action, rather than the empty lambda that
                        // used to stand here and left a reader with a sentence and nowhere to press.
                        LButton(
                            t("app.settings.webhooks.name_a_url", "Name a URL"),
                            { (document.querySelector("#$WEBHOOK_FORM_ID input") as? HTMLElement)?.focus() },
                            variant = LButtonVariant.SECONDARY,
                            leadingIcon = { LWebhookIcon() },
                        )
                    }
                } else {
                    // The cards share one wrapper here, so the space between them is set here too —
                    // the page column's gap falls outside it and no longer separates them.
                    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
                        hooks.forEachIndexed { index, hook ->
                            HookCard(hook, index) { deleting = hook }
                        }
                    }
                }
            }

            val pending = deleting
            LModal(
                open = pending != null,
                title = t("app.settings.webhooks.remove_this_webhook", "Remove this webhook?"),
                onClose = { if (!busy) deleting = null },
                actions = {
                    LButton(
                        t("app.settings.webhooks.keep_webhook", "Keep webhook"),
                        { deleting = null },
                        variant = LButtonVariant.GHOST,
                        enabled = !busy,
                    )
                    LButton(
                        label = t("app.settings.webhooks.remove_webhook", "Remove webhook"),
                        onClick = {
                            val hook = pending ?: return@LButton
                            val org = slug ?: return@LButton
                            scope.launch {
                                busy = true
                                try {
                                    ApiClient.deleteWebhook(org, hook.id)
                                    message = null
                                    deleting = null
                                    refreshKey++
                                } catch (cancelled: CancellationException) {
                                    throw cancelled
                                } catch (failure: Throwable) {
                                    message = failure.message?.takeIf(String::isNotBlank)
                                        ?: webhookRemovalRefused()
                                } finally {
                                    busy = false
                                }
                            }
                        },
                        variant = LButtonVariant.DANGER,
                        enabled = !busy,
                        loading = busy,
                    )
                },
            ) {
                LAlert(
                    t("app.settings.webhooks.deliveries_stop_immediately", "Deliveries stop immediately"),
                    tf(
                        "app.settings.webhooks.lingvo_will_stop_calling_url",
                        "Lingvo will stop calling {url}. Its signing secret cannot be recovered, so " +
                            "recreating this endpoint means giving your receiver a new one.",
                        "url" to pending?.url.orEmpty(),
                    ),
                    variant = LAlertVariant.DANGER,
                )
            }
        }
    }
}

@Composable
private fun HookCard(hook: WebhookDto, index: Int, onDelete: () -> Unit) {
    LCard(Modifier.fillMaxWidth().enterStaggered(index)) {
        Column(Modifier.fillMaxWidth().gap(Space.XS)) {
            Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                // **The badge beside this used to lead nowhere.** A webhook Lingvo switched off
                // after twenty failures said so, and the only control on the card was Remove —
                // which mints a new signing secret and breaks the receiver that was about to start
                // working again. The detail page holds the delivery log, replay, and the re-enable
                // switch; nothing in the app constructed its URL, and the id is a UUID, so it was
                // reachable only by typing one out.
                Link(
                    path = "/app/settings/webhooks/${hook.id}",
                    modifier = Modifier.flexGrow(1).monoFont().fontSize(TypeScale.Small),
                ) {
                    Text(hook.url)
                }
                when {
                    // Visible on the list rather than behind a log: a webhook that stopped working
                    // three weeks ago and says nothing is the failure this column exists to prevent.
                    !hook.enabled -> LBadge(t("app.settings.webhooks.switched_off_after_failures", "switched off after failures"), variant = LBadgeVariant.DANGER)
                    // A count, so it needs the reader's own plural rule rather than English's: one
                    // failure is "1 failure" here and takes a third form in Polish.
                    hook.consecutiveFailures > 0 -> LBadge(
                        tPlural(
                            "app.settings.webhooks.failures",
                            hook.consecutiveFailures,
                            "{count} failure",
                            "{count} failures",
                        ),
                        variant = LBadgeVariant.WARNING,
                    )
                    // The status code stays outside the catalogue — it is a number, not copy — so
                    // the sentence around it is a placeholder a translator can move.
                    hook.lastStatus != null -> LBadge(
                        tf(
                            "app.settings.webhooks.last_status",
                            "last: {status}",
                            "status" to hook.lastStatus.toString(),
                        ),
                        variant = LBadgeVariant.SUCCESS,
                    )
                    else -> LBadge(t("app.settings.webhooks.never_fired", "never fired"))
                }
                // Every card on this page draws a button reading "Remove"; the URL is what tells
                // them apart, and only the visible layout was carrying it.
                LButton(
                    t("app.settings.webhooks.remove", "Remove"),
                    onDelete,
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                    accessibleLabel = webhookRowRemoveLabel(hook.url),
                )
            }
            // **The workspace cannot discover this by looking.** Every delivery came back 200; only
            // the receiver's verification failed, silently, on the other side of the wire.
            if (!hook.signatureVerifiable) {
                LAlert(
                    t("app.settings.webhooks.this_webhooks_signature_cannot_be_verified", "This webhook's signature cannot be verified"),
                    t("app.settings.webhooks.it_was_created_before_lingvo_stored", "It was created before Lingvo stored signing secrets in a form it could sign " +
                        "with, so the X-Lingvo-Signature header on its deliveries will never match " +
                        "what your receiver computes. Open it above and rotate the secret — the " +
                        "webhook keeps its id and its whole delivery history."),
                    variant = LAlertVariant.WARNING,
                )
            }
            LMuted(hook.events.joinToString(", ") { it.id })
        }
    }
}

/** The card the header action and the empty state both put the caret into. */
private const val WEBHOOK_FORM_ID = "webhook-add-form"

/**
 * What one endpoint's Remove button is called to a screen reader.
 *
 * `"${t("…remove", "Remove")} ${hook.url}"` translated the verb and left the join in English word
 * order, and a Kotlin template is one expression to the extractor rather than a message with a
 * parameter in it.
 */
internal fun webhookRowRemoveLabel(url: String): String = trf(
    "app.settings.webhooks.remove_url",
    "Remove {url}",
    "url" to url,
)

/**
 * What a failed deletion says.
 *
 * Uncatalogued, and it also skipped the `takeIf(String::isNotBlank)` guard every sibling uses — so
 * a server that answered with a blank message rendered an empty alert saying nothing at all.
 */
internal fun webhookRemovalRefused(): String = tr(
    "app.settings.webhooks.couldnt_remove_this_webhook_retry",
    "Couldn't remove this webhook. Retry?",
)
